import { UseMutateFunction, useMutation } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { arkivinfoKeys, arkivinfoQueryHelpers } from '../queries/helpers'
import { arkivinfoApi } from '../queries/arkivinfo-api'
import { IArkivinfoSkjermedeOrdMutateVars } from '../types'
import { Draft } from 'immer'
import { DebounceMutate, useDebounceMutate } from '../../../common/query'
import { useCallback } from 'react'
import { DEBOUNCE_MS } from '../../../constants'

export { useUpdateSkjermedeOrd }

const useUpdateSkjermedeOrd = (kvitteringId: string) => {
  const mutation = useMutation<
    AxiosResponse<null>,
    string | Error,
    IArkivinfoSkjermedeOrdMutateVars
  >({
    mutationKey: arkivinfoKeys.updateSkjermetOrd(),
    mutationFn: arkivinfoApi.updateSkjermedeOrd,
    onSuccess: async (_, { kvitteringId }) => {
      await arkivinfoQueryHelpers.invalidate(kvitteringId)
    },
  })

  const optimisticDebounceUpdate = useOptimisticDebounceArkivinfoMutate(
    mutation.mutate,
    kvitteringId ?? '',
    (draft, { skjermedeOrd }) => {
      if (!draft) return draft

      return { ...draft, skjermedeOrd }
    }
  )

  const updateFn = useCallback(
    (payload: IArkivinfoSkjermedeOrdMutateVars) => {
      const updateKey = JSON.stringify(arkivinfoKeys.updateSkjermetOrd()[0])

      optimisticDebounceUpdate(payload, {
        debounceMs: DEBOUNCE_MS,
        key: `${updateKey}_${payload.kvitteringId}`,
      })
    },
    [optimisticDebounceUpdate]
  )

  return { ...mutation, mutate: updateFn }
}

const useOptimisticDebounceArkivinfoMutate = <
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  mutate: UseMutateFunction<TData, TError, TVariables, TContext>,
  kvitteringId: string,
  updateFn: (
    draft: Draft<ITilsynskvitteringRemote.ArkivInfoRes>,
    variables: TVariables
  ) => void
) => {
  const debounceMutate = useDebounceMutate(mutate)

  return useCallback<DebounceMutate<TData, TError, TVariables, TContext>>(
    (variables, options) => {
      arkivinfoQueryHelpers.updateCache(kvitteringId, (draft) =>
        updateFn(draft, variables)
      )

      debounceMutate(variables, options)
    },
    [debounceMutate, kvitteringId, updateFn]
  )
}
